import { Outlet, useMatch } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { AppShell, type NavSection } from '@solid/ui';
import { ProjectNav } from './ProjectNav';
import { ProjectTopbar } from './ProjectTopbar';
import { AppBarFallback } from './AppBarItems';
import { FavoritesPanel } from './FavoritesPanel';

/**
 * No global nav sections: the sidebar is the project nav (ProjectNav), so
 * every screen outside it names itself here. Unlisted routes print the brand.
 */
const NO_SECTIONS: NavSection[] = [];
const TITLES: Array<[string, string]> = [
  ['/projects', 'Projects'],
  ['/activity', 'Activity'],
  ['/tasks', 'My tasks'],
  ['/announcements', 'Announcements'],
  ['/forms', 'Forms'],
  ['/organizations', 'Organizations'],
  ['/settings', 'Settings'],
  ['/help', 'Support'],
  ['/dashboard', 'Dashboard'],
];

/**
 * The one page frame, from @solid/ui — the same shell sales, monitoring and
 * the accounts portal wear.
 *
 * On the app rail: the rail script sets `body { margin-left }`, which the
 * shell's sticky, in-flow sidebar inherits. So unlike the old fixed sidebar,
 * nothing here adds a rail offset — doing so would double it. That also means
 * IS_STANDALONE needs no special case: no rail, no body margin, no offset.
 *
 * No hub-wide search (Jesse, 2026-11-04: "we don't need the global search").
 * A project page's own search lives in its header, which IS the topbar.
 */
export function AppLayout() {
  // A project page puts its header in the topbar instead of the shell's
  // "Projects" title, so the page has one header rather than two.
  const projectMatch = useMatch('/projects/:projectId/*');
  const projectId = projectMatch?.params.projectId;

  return (
    <AppShell
      brand={{ name: 'Solid Hub', mark: <MessageSquare /> }}
      sections={NO_SECTIONS}
      offNavTitles={TITLES}
      sidebarExtra={({ onNavigate }) => <ProjectNav onNavigate={onNavigate} />}
      topbar={projectId ? <ProjectTopbar key={projectId} projectId={projectId} /> : undefined}
      // Favorites and Activity live on the app bar; without one (standalone
      // dev) they'd have no way in, so they sit here instead.
      topbarEnd={<AppBarFallback />}
      contentMaxWidth={projectId ? '100%' : 1600}
    >
      <Outlet />
      <FavoritesPanel />
    </AppShell>
  );
}
